import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X } from 'lucide-react';
import { ChatThreadList } from './ChatThreadList';
import { ChatConversation } from './ChatConversation';
import useChatStream from '../../hooks/useChatStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Floating customer-chat dock for the CRM. Mounted once in CrmLayout so staff
 * can keep chatting with customers while navigating any CRM section. Flashes
 * when there are unread customer messages.
 */
export const CrmChatDock = () => {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/threads/unread-count`, { withCredentials: true });
      setUnread(res.data.unread || 0);
    } catch { /* noop */ }
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/threads`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 20000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => { if (open) { fetchThreads(); fetchUnread(); } }, [open, fetchThreads, fetchUnread]);

  useChatStream(useCallback((evt) => {
    if (evt?.type === 'thread_message' || evt?.type === 'thread_read') {
      fetchUnread();
      if (open) fetchThreads();
    }
  }, [open, fetchUnread, fetchThreads]));

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed bottom-20 right-[88px] z-50 w-14 h-14 rounded-full bg-[#118AB2] hover:bg-[#0E7399] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-110 ${unread > 0 ? 'animate-pulse' : ''}`}
          data-testid="crm-chat-dock-btn"
          aria-label="Customer messages"
          title="Customer messages"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[#FF3B6B] text-white text-[11px] font-bold flex items-center justify-center shadow ring-2 ring-white" data-testid="crm-chat-dock-unread">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 bg-white rounded-2xl shadow-2xl border border-[#E8E5DF] flex flex-col sm:w-[400px] sm:h-[580px] w-[calc(100vw-24px)] h-[calc(100vh-150px)] max-h-[80vh] overflow-hidden"
          data-testid="crm-chat-dock-panel"
        >
          <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-[#118AB2] to-[#0E7399] text-white flex-shrink-0">
            <p className="font-semibold text-sm" style={{ fontFamily: 'Fredoka' }}>Customer Messages</p>
            <button onClick={() => { setOpen(false); setActive(null); }} className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full" data-testid="crm-chat-dock-close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {active ? (
              <ChatConversation apiBase="staff" threadId={active} onBack={() => setActive(null)} onActivity={() => { fetchUnread(); fetchThreads(); }} />
            ) : (
              <div className="h-full overflow-y-auto">
                <ChatThreadList threads={threads} activeThreadId={active} onSelect={(t) => setActive(t.thread_id)} emptyText="No customer conversations yet." />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CrmChatDock;
